import { useContext, useState, useCallback, useEffect } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { useLoading } from 'application/contexts/useLoading';
import { TransferListQuery } from 'interfaceAdapter/queries/TransferList';
import { BankAccountQuery } from 'interfaceAdapter/queries/BankAccount';
import { BankAccountRepository } from 'interfaceAdapter/repositories/BankAccount';
import { TransferApplyRepository } from 'interfaceAdapter/repositories/TransferApply';
import { ITransferListQuery } from 'application/querySchemas/ITransferListQuery';
import { IBankAccountQuery } from 'application/querySchemas/IBankAccountQuery';
import { IBankAccountRepository } from 'application/repositorySchemas/IBankAccountRepository';
import { ITransferApplyRepository } from 'application/repositorySchemas/ITransferApplyRepository';
import { TransferItem } from 'ui/components/supporter/patterns/TransferList/TransferListTable';
import { BankAccount } from 'ui/components/supporter/patterns/TransferList/BankAccountManagement';

export const useSupporterTransferList = () => {
    const diContainer = useContext(diContainerContext);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const transferListQuery = diContainer.resolve<ITransferListQuery>(TransferListQuery);
    const bankAccountQuery = diContainer.resolve<IBankAccountQuery>(BankAccountQuery);
    const bankAccountRepository = diContainer.resolve<IBankAccountRepository>(BankAccountRepository);
    const transferApplyRepository = diContainer.resolve<ITransferApplyRepository>(TransferApplyRepository);

    const [transfers, setTransfers] = useState<TransferItem[]>([]);
    const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);

    const fetchTransfers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await transferListQuery.fetch();
            setTransfers(data);
        } catch (error) {
            setSnackbarMessage('振込一覧の取得に失敗しました。');
            setShowSnackbar(true);
            console.error('振込一覧取得エラー:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBankAccount = useCallback(async () => {
        try {
            setLoading(true);
            const data = await bankAccountQuery.fetch();
            setBankAccount(data);
        } catch (error) {
            setSnackbarMessage('銀行口座の取得に失敗しました。');
            setShowSnackbar(true);
            console.error('銀行口座取得エラー:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const saveBankAccount = useCallback(
        async (account: Partial<BankAccount>) => {
            try {
                setLoading(true);
                await bankAccountRepository.save(account);
                setSnackbarMessage('銀行口座を保存しました。');
                setShowSnackbar(true);
                await fetchBankAccount();
            } catch (error) {
                setSnackbarMessage('銀行口座の保存に失敗しました。');
                setShowSnackbar(true);
                console.error('銀行口座保存エラー:', error);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const applyTransfer = useCallback(
        async (transferId: number, accountNumber: string) => {
            try {
                setLoading(true);
                await transferApplyRepository.apply(transferId, accountNumber);
                setSnackbarMessage('振込申請が完了しました。');
                setShowSnackbar(true);
                await fetchTransfers();
            } catch (error) {
                setSnackbarMessage('振込申請に失敗しました。');
                setShowSnackbar(true);
                console.error('振込申請エラー:', error);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        fetchTransfers();
        fetchBankAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        transfers,
        bankAccount,
        saveBankAccount,
        applyTransfer,
        fetchTransfers,
        fetchBankAccount,
    };
};
